// src/services/authService.js
import { auth, db } from "firebase.js";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";
import {
  getDoc,
  setDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import * as Sentry from "@sentry/react";
import { authError, reportAuthError } from "services/authErrors";

export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Turn what people type ("514 555-1234", "(514) 555.1234", "1-514-555-1234",
 * "+33 6 12 34 56 78") into E.164. Bare 10-digit numbers are North American.
 * Returns null when it cannot be a phone number.
 */
export const normalizePhone = (raw) => {
  let phone = raw.trim().replace(/[\s().-]/g, "");
  if (phone.startsWith("00")) phone = `+${phone.slice(2)}`;
  if (/^\d{10}$/.test(phone)) phone = `+1${phone}`;
  else if (/^1\d{10}$/.test(phone)) phone = `+${phone}`;
  return /^\+[1-9]\d{7,14}$/.test(phone) ? phone : null;
};

const profileFrom = (user, { name, phone } = {}) => ({
  uid: user.uid,
  name: name || user.displayName || user.email.split("@")[0],
  email: user.email,
  phone: phone || "",
  role: "user",
  // The rules only accept true when the ID token says the email is confirmed
  verified: user.emailVerified,
  createdAt: serverTimestamp(),
});

/**
 * Create the account, its profile, and send the confirmation email, then sign out:
 * logging in stays refused until the email is confirmed.
 *
 * Once the Auth account exists, nothing here throws: a failure would leave an
 * account the user cannot finish setting up, since signing up again fails with
 * "email already in use". A missing profile is recreated at the first login, and
 * a confirmation email that failed to send can be sent again from the pages.
 */
export const registerUser = async (email, password, name, phone) => {
  email = email.trim();
  if (!isValidEmail(email)) throw authError("auth/invalid-email");

  const { user } = await createUserWithEmailAndPassword(auth, email, password);

  // Keep the name on the Auth account too, so a lost profile can be rebuilt with it
  try {
    await updateProfile(user, { displayName: name });
  } catch (error) {
    Sentry.captureException(error);
  }

  try {
    await setDoc(doc(db, "users", user.uid), profileFrom(user, { name, phone }));
  } catch (error) {
    Sentry.captureException(error);
  }

  let emailSent = true;
  try {
    await sendEmailVerification(user);
  } catch (error) {
    emailSent = false;
    reportAuthError(error);
  }

  await signOut(auth).catch(() => {});
  return { email: user.email, emailSent };
};

/**
 * Sign in, refusing accounts whose email is not confirmed, unless a master
 * verified the profile by hand from the admin dashboard (the rescue for people
 * who never get the email). Also repairs the profile: recreates it when missing
 * and copies the confirmed email to profile.verified.
 */
export const loginUser = async (email, password) => {
  const { user } = await signInWithEmailAndPassword(auth, email.trim(), password);

  // The session caches emailVerified; the link may have been clicked since
  await user.reload();

  const userRef = doc(db, "users", user.uid);
  const snapshot = await getDoc(userRef);
  const profile = snapshot.exists() ? snapshot.data() : null;

  if (!user.emailVerified && profile?.verified !== true) {
    await signOut(auth);
    throw authError("fqst/email-not-verified");
  }

  const syncVerified = user.emailVerified && profile?.verified !== true;
  // The rules read email_verified from the ID token, which only updates on refresh
  if (syncVerified) await user.getIdToken(true);

  if (!profile) {
    Sentry.captureMessage("Recreated a missing user profile at login", "warning");
    await setDoc(userRef, profileFrom(user));
    return user;
  }

  const fixes = {};
  if (syncVerified) fixes.verified = true;
  if (!profile.createdAt) fixes.createdAt = new Date(user.metadata.creationTime);
  if (Object.keys(fixes).length > 0) {
    // Not worth refusing the login over: the next login tries again
    await updateDoc(userRef, fixes).catch((error) => Sentry.captureException(error));
  }
  return user;
};

// Logout user
export const logoutUser = async () => {
  await signOut(auth);
};

/**
 * Send the confirmation email again. It needs the password: Firebase only sends
 * it to a signed-in user, and the user is signed out while unconfirmed.
 * Returns { alreadyVerified } so the page can tell them to just log in.
 */
export const resendVerificationEmail = async (email, password) => {
  email = email.trim();
  if (!email) throw authError("auth/missing-email");
  if (!password) throw authError("auth/missing-password");

  const { user } = await signInWithEmailAndPassword(auth, email, password);
  try {
    await user.reload();
    if (user.emailVerified) return { alreadyVerified: true };
    await sendEmailVerification(user);
    return { alreadyVerified: false };
  } finally {
    await signOut(auth).catch(() => {});
  }
};

/**
 * Send a password reset link. Email enumeration protection is on in Firebase,
 * so this succeeds even when no account uses that address: the page must not
 * claim the email was sent. Without that protection (e.g. the emulator) an unknown
 * address is treated the same way, so the page never reveals who has an account.
 */
export const resetPassword = async (email) => {
  email = email.trim();
  if (!email) throw authError("auth/missing-email");
  if (!isValidEmail(email)) throw authError("auth/invalid-email");
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    if (error.code !== "auth/user-not-found") throw error;
  }
};
